/**
 * Institution Compliance Section Component
 */

import React from 'react';
import { Grid, Column, Button, Tag } from '@carbon/react';
import { DocumentPdf, Certificate, Security } from '@carbon/icons-react';
import type { ComplianceDoc } from '../../../../shared/interfaces/InstitutionalLanding';

interface InstitutionComplianceSectionProps {
  documents: ComplianceDoc[];
  institutionName: string;
}

const InstitutionComplianceSection: React.FC<InstitutionComplianceSectionProps> = ({ documents, institutionName }) => {
  const getComplianceIcon = (type: string) => {
    switch (type) {
      case 'ferpa':
      case 'coppa':
        return <Security size={20} />;
      case 'gdpr':
      case 'ccpa':
        return <Certificate size={20} />;
      default:
        return <DocumentPdf size={20} />;
    }
  };

  const getComplianceColor = (type: string) => {
    switch (type) {
      case 'ferpa':
        return 'blue';
      case 'coppa':
        return 'green';
      case 'gdpr':
        return 'purple';
      case 'ccpa':
        return 'cyan';
      default:
        return 'gray';
    }
  };

  return (
    <section className="institution-compliance">
      <Grid narrow>
        <Column lg={16}>
          <h2>Compliance & Security</h2>
          <p className="section-subtitle">
            {institutionName} maintains the highest standards of data privacy and security
          </p>
        </Column>
        {documents.map((doc, index) => (
          <Column key={index} lg={8} md={4}>
            <div className="compliance-card">
              <div className="compliance-header">
                {getComplianceIcon(doc.type)}
                <Tag type={getComplianceColor(doc.type)} size="sm">
                  {doc.type.toUpperCase()}
                </Tag>
              </div>
              <h3>{doc.title}</h3>
              {doc.description && <p>{doc.description}</p>}
              <div className="compliance-footer">
                <span className="last-updated">Updated: {new Date(doc.lastUpdated).toLocaleDateString()}</span>
                <Button
                  kind="ghost"
                  size="sm"
                  renderIcon={DocumentPdf}
                  onClick={() => window.open(doc.pdfUrl, '_blank')}
                >
                  View Document
                </Button>
              </div>
            </div>
          </Column>
        ))}
      </Grid>
      <style>{`
        .institution-compliance {
          padding: 4rem 0;
          background-color: var(--cds-layer-01);
        }
        .institution-compliance h2 {
          font-size: 2.5rem;
          text-align: center;
          margin-bottom: 1rem;
        }
        .section-subtitle {
          text-align: center;
          color: var(--cds-text-secondary);
          margin-bottom: 3rem;
        }
        .compliance-card {
          background-color: var(--cds-background);
          padding: 1.5rem;
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        .compliance-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1rem;
          color: var(--cds-interactive-01);
        }
        .compliance-card h3 {
          font-size: 1.125rem;
          margin-bottom: 0.75rem;
        }
        .compliance-card p {
          font-size: 0.875rem;
          color: var(--cds-text-secondary);
          flex-grow: 1;
        }
        .compliance-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 1.5rem;
          padding-top: 1rem;
          border-top: 1px solid var(--cds-border-subtle);
        }
        .last-updated {
          font-size: 0.75rem;
          color: var(--cds-text-secondary);
        }
      `}</style>
    </section>
  );
};

export default InstitutionComplianceSection;
